import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import { IFilter, IOption } from "@/domain/entities/filter/filter.types";
import useConvertNumberToTimeText from "@/use/useConvertNumberToTime";
import { ref, computed, PropType, watch, onMounted, onUnmounted } from "vue";


const __default__ = {
  name: "TimeSlider2",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  title: {
    type: String,
  },
  minHourRange: {
    type: Number,
    required: true,
  },
  maxHourRange: {
    type: Number,
    required: true,
  },
  step: {
    type: Number,
    default: 1,
  },
  labelStep: {
    type: Number,
    default: 1,
  },
  fromTimeFilter: {
    type: Object as PropType<IFilter>,
    required: true,
  },
  toTimeFilter: {
    type: Object as PropType<IFilter>,
    required: true,
  },
},
  emits: ["onUpdateFromTime", "onUpdateToTime"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

_useCssVars(_ctx => ({
  "e0e7dbac-left": (left.value),
  "e0e7dbac-right": (right.value)
}))

const props = __props;

const emit = __emit;

const convertTimeToHour = (time: string) => {
  return Number(time.split(":")?.[0] ?? props.minHourRange);
};

const defaultFromHour = computed(() => {
  const fromTime = props.fromTimeFilter.availableOptions.find(
    (option) => option.default
  );
  return fromTime?.value
    ? convertTimeToHour(fromTime.value)
    : props.minHourRange;
});
const defaultToHour = computed(() => {
  const toTime = props.toTimeFilter.availableOptions.find(
    (option) => option.default
  );
  return toTime?.value ? convertTimeToHour(toTime.value) : props.maxHourRange;
});

const fromHour = ref(defaultFromHour.value);
const toHour = ref(defaultToHour.value);

// 슬라이더 트랙 엘리먼트 참조
const rangeInputRef = ref<HTMLElement | null>(null);
// 현재 드래그 중인 썸 타입 ('min' or 'max')
const activeThumb = ref<"min" | "max" | null>(null);

const getRangeLabel = computed(() => {
  const rangeLabel: string[] = [];
  let count = 0;
  let hour = props.minHourRange + props.labelStep * count;
  while (hour >= props.minHourRange && hour <= props.maxHourRange) {
    rangeLabel.push(useConvertNumberToTimeText(hour));
    count += 1;
    hour = props.minHourRange + props.labelStep * count;
  }
  return rangeLabel;
});

const thumbMinLeft = computed(() => {
  const totalRange = props.maxHourRange - props.minHourRange;
  if (totalRange === 0) return "0%";
  const percentage = (fromHour.value - props.minHourRange) / totalRange;
  return `calc(${percentage * 100}% - 20px)`;
});

const thumbMaxLeft = computed(() => {
  const totalRange = props.maxHourRange - props.minHourRange;
  if (totalRange === 0) return "0%";
  const percentage = (toHour.value - props.minHourRange) / totalRange;
  return `calc(${percentage * 100}% - 20px)`;
});

const left = computed(() => {
  const totalRange = props.maxHourRange - props.minHourRange;
  if (totalRange === 0) return "0%";
  const percentage = (fromHour.value - props.minHourRange) / totalRange;
  return percentage * 100 + "%";
});

const right = computed(() => {
  const totalRange = props.maxHourRange - props.minHourRange;
  if (totalRange === 0) return "100%";
  const percentage = (toHour.value - props.minHourRange) / totalRange;
  return 100 - percentage * 100 + "%";
});

const emitOnUpdateFromTime = (newFromHour: number) => {
  const fromTimeOption: IOption = {
    key: "fromDate",
    name: "fromDate",
    value: useConvertNumberToTimeText(newFromHour),
    default:
      newFromHour === defaultFromHour.value &&
      toHour.value === defaultToHour.value,
  };
  emit("onUpdateFromTime", props.fromTimeFilter, fromTimeOption);
};

const emitOnUpdateToTime = (newToHour: number) => {
  const toTimeOption: IOption = {
    key: "toTime",
    name: "toTime",
    value: useConvertNumberToTimeText(newToHour),
    default:
      fromHour.value === defaultFromHour.value &&
      newToHour === defaultToHour.value,
  };
  emit("onUpdateToTime", props.toTimeFilter, toTimeOption);
};

// 드래그 시작 함수
const startDrag = (
  thumbType: "min" | "max",
  event: MouseEvent | TouchEvent
) => {
  activeThumb.value = thumbType;
  event.preventDefault(); // 이벤트의 기본 동작 방지 (특히 터치 이벤트에서 스크롤 방지)

  document.addEventListener("mousemove", onDrag);
  document.addEventListener("mouseup", endDrag);
  document.addEventListener("touchmove", onDrag, { passive: false });
  document.addEventListener("touchend", endDrag);
};

// 드래그 중 함수
const onDrag = (event: MouseEvent | TouchEvent) => {
  if (!activeThumb.value || !rangeInputRef.value) return;

  const clientX =
    (event as MouseEvent).clientX || (event as TouchEvent).touches[0].clientX;
  const sliderRect = rangeInputRef.value.getBoundingClientRect();

  // 썸의 중심이 트랙 끝에 정확히 위치하도록 보정된 슬라이더 너비
  // rangeInputRef.value의 너비는 이미 margin 0 16px이 적용된 트랙의 유효 너비
  // offsetX 계산 시, 썸의 절반 너비를 고려하여 시작점을 보정합니다.
  const offsetX = clientX - sliderRect.left;

  // 썸의 중심이 트랙의 시작점(0%)과 끝점(100%)에 정확히 오도록 보정된 계산
  // 썸 너비 30px 중 15px(절반)이 양쪽 끝에 걸침
  const effectiveTrackWidth = sliderRect.width;
  let newPercentage = offsetX / effectiveTrackWidth;

  newPercentage = Math.max(0, Math.min(1, newPercentage));

  const totalRange = props.maxHourRange - props.minHourRange;
  let newHour = props.minHourRange + newPercentage * totalRange;

  newHour = Math.round(newHour / props.step) * props.step;
  newHour = Math.max(props.minHourRange, Math.min(props.maxHourRange, newHour));

  if (activeThumb.value === "min") {
    // fromHour는 minHourRange <= fromHour <= (toHour - step) 범위
    newHour = Math.max(
      props.minHourRange,
      Math.min(newHour, toHour.value - props.step)
    );
    fromHour.value = newHour;
    emitOnUpdateFromTime(fromHour.value);
    emitOnUpdateToTime(toHour.value); // toHour 값도 함께 업데이트 (default 값 설정 때문)
  } else if (activeThumb.value === "max") {
    // toHour는 (fromHour + step) <= toHour <= maxHourRange 범위
    newHour = Math.max(
      fromHour.value + props.step,
      Math.min(newHour, props.maxHourRange)
    );
    toHour.value = newHour;
    emitOnUpdateFromTime(fromHour.value); // fromHour 값도 함께 업데이트
    emitOnUpdateToTime(toHour.value);
  }
};

// 드래그 종료 함수
const endDrag = () => {
  activeThumb.value = null;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", endDrag);
  document.removeEventListener("touchmove", onDrag);
  document.removeEventListener("touchend", endDrag);
};

// 키보드 상호작용
const onKeyDown = (thumbType: "min" | "max", event: KeyboardEvent) => {
  let newValue = thumbType === "min" ? fromHour.value : toHour.value;
  const isMinThumb = thumbType === "min";
  const stepValue = props.step; // 기본 스텝
  const largeStepValue = props.step * 10; // PageUp/PageDown 큰 스텝

  switch (event.key) {
    case "ArrowRight":
    case "ArrowUp":
      newValue += stepValue;
      break;
    case "ArrowLeft":
    case "ArrowDown":
      newValue -= stepValue;
      break;
    case "Home":
      newValue = isMinThumb ? props.minHourRange : props.minHourRange; // Home키는 minThumb, maxThumb 모두 minHourRange로 이동
      break;
    case "End":
      newValue = isMinThumb ? props.maxHourRange : props.maxHourRange; // End키는 minThumb, maxThumb 모두 maxHourRange로 이동
      break;
    case "PageUp":
      newValue += largeStepValue;
      break;
    case "PageDown":
      newValue -= largeStepValue;
      break;
    default:
      return; // 다른 키는 무시
  }

  // 값 범위 제한 및 상호 제약 조건 적용
  if (isMinThumb) {
    newValue = Math.max(
      props.minHourRange,
      Math.min(newValue, toHour.value - props.step) // fromHour는 toHour보다 step 이상 작아야 함
    );
    if (fromHour.value !== newValue) {
      fromHour.value = newValue;
      emitOnUpdateFromTime(fromHour.value);
      emitOnUpdateToTime(toHour.value);
    }
  } else {
    newValue = Math.max(
      fromHour.value + props.step, // toHour는 fromHour보다 step 이상 커야 함
      Math.min(newValue, props.maxHourRange)
    );
    if (toHour.value !== newValue) {
      toHour.value = newValue;
      emitOnUpdateFromTime(fromHour.value);
      emitOnUpdateToTime(toHour.value);
    }
  }
  event.preventDefault(); // 기본 스크롤 방지
};

// 필터 변경 감지 및 내부 상태 업데이트 (기존 로직 유지)
watch(
  () => props.fromTimeFilter.selectedOptions,
  (newVal) => {
    const newFromTime = Array.isArray(newVal)
      ? newVal[0]?.value
      : newVal?.value;
    const newFromHour = newFromTime
      ? convertTimeToHour(newFromTime)
      : defaultFromHour.value;
    if (fromHour.value !== newFromHour) {
      fromHour.value = newFromHour;
    }
  },
  { deep: true }
);

watch(
  () => props.toTimeFilter.selectedOptions,
  (newVal) => {
    const newToTime = Array.isArray(newVal) ? newVal[0]?.value : newVal?.value;
    const newToHour = newToTime
      ? convertTimeToHour(newToTime)
      : defaultToHour.value;
    if (toHour.value !== newToHour) {
      toHour.value = newToHour;
    }
  },
  { deep: true }
);

// 컴포넌트 마운트 시 rangeInputRef 초기화
onMounted(() => {
  // `rangeInputRef`가 `template`에 ref로 연결되므로, 마운트 시점에 접근 가능
});

onUnmounted(() => {
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", endDrag);
  document.removeEventListener("touchmove", onDrag);
  document.removeEventListener("touchend", endDrag);
});

const __returned__ = { props, emit, convertTimeToHour, defaultFromHour, defaultToHour, fromHour, toHour, rangeInputRef, activeThumb, getRangeLabel, thumbMinLeft, thumbMaxLeft, left, right, emitOnUpdateFromTime, emitOnUpdateToTime, startDrag, onDrag, endDrag, onKeyDown, get useConvertNumberToTimeText() { return useConvertNumberToTimeText } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})